// LORA Reviews — eigenes Web Component fuer judge.me, gerendert in LORA-Markup.
// Nutzt judge.me's Widget API (Public Token kompatibel) via Cloudflare Worker
// Proxy. Component parst das eingebettete Widget-HTML mit DOMParser und
// extrahiert strukturierte Daten — kein jdgm-* CSS, kein widget-JS-Bundle.
class LoraReviews extends HTMLElement {
  connectedCallback() {
    if (this._mounted) return;
    this._mounted = true;

    this.productId = this.dataset.productId;
    this.shopDomain = this.dataset.shopDomain;
    this.proxyUrl = (this.dataset.proxyUrl || '').replace(/\/$/, '');
    this.apiToken = this.dataset.apiToken;
    this.perPage = parseInt(this.dataset.perPage, 10) || 6;
    this.productHandle = this.dataset.productHandle;
    this.productTitle = this.dataset.productTitle || '';
    this.writeReviewLabel = this.dataset.writeReviewLabel || 'Bewertung schreiben';
    this.page = 1;
    this.state = { reviews: [], total: 0, average: 0, distribution: {}, pageCount: 1 };
    this.judgemeProductUuid = null;

    console.log('[lora-reviews] init', {
      productId: this.productId,
      shopDomain: this.shopDomain,
      mode: this.proxyUrl ? 'proxy' : (this.apiToken ? 'direct' : 'unconfigured'),
      proxyUrl: this.proxyUrl || null,
    });

    if (!this.proxyUrl && !this.apiToken) {
      this.renderEmptyState('Reviews nicht konfiguriert — Worker Proxy URL in Theme-Settings → judge.me Reviews hinterlegen.');
      return;
    }

    this.useIntersectionLoad();
  }

  useIntersectionLoad() {
    if (!('IntersectionObserver' in window)) return this.load();
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        io.disconnect();
        this.load();
      }
    }, { rootMargin: '200px 0px' });
    io.observe(this);
  }

  async load() {
    try {
      const data = await this.fetchWidget(this.page);
      const parsed = this.parseWidgetHtml(data.widget || '');
      this.state = {
        reviews: parsed.reviews,
        total: parsed.total,
        average: parsed.average,
        distribution: parsed.distribution,
        pageCount: parsed.pageCount,
      };
      if (parsed.judgemeProductUuid) this.judgemeProductUuid = parsed.judgemeProductUuid;

      console.log('[lora-reviews] loaded', {
        total: parsed.total,
        average: parsed.average,
        reviews: parsed.reviews.length,
        pageCount: parsed.pageCount,
        judgemeProductUuid: this.judgemeProductUuid,
      });

      this.render();
    } catch (err) {
      console.error('[lora-reviews] fetch failed', err);
      this.renderEmptyState('Bewertungen konnten nicht geladen werden.');
    }
  }

  async fetchWidget(page) {
    const params = new URLSearchParams({
      external_id: this.productId,
      handle: this.productHandle || '',
      page: String(page),
      per_page: String(this.perPage),
    });
    let url;
    if (this.proxyUrl) {
      url = `${this.proxyUrl}/widget?${params}`;
    } else {
      params.set('api_token', this.apiToken);
      params.set('shop_domain', this.shopDomain);
      url = `https://judge.me/api/v1/widgets/product_review?${params}`;
    }
    const res = await fetch(url);
    if (!res.ok) throw new Error(`widget ${res.status}`);
    return res.json();
  }

  parseWidgetHtml(html) {
    const result = { reviews: [], total: 0, average: 0, distribution: {}, pageCount: 1, judgemeProductUuid: null };
    if (!html) return result;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const root = doc.querySelector('.jdgm-rev-widg');
    if (root) {
      result.total = parseInt(root.getAttribute('data-number-of-reviews') || '0', 10);
      result.average = parseFloat(root.getAttribute('data-average-rating') || '0');
    }

    // Reviews
    doc.querySelectorAll('.jdgm-rev').forEach((el) => {
      const ratingAttr = el.getAttribute('data-rating') || el.querySelector('[data-score]')?.getAttribute('data-score');
      const timestampEl = el.querySelector('.jdgm-rev__timestamp, [data-content-timestamp]');
      const dateIso = timestampEl?.getAttribute('data-content-timestamp')
        || timestampEl?.getAttribute('datetime')
        || el.getAttribute('data-published-at')
        || '';
      const pictures = Array.from(el.querySelectorAll('.jdgm-rev__pic img, .jdgm-rev__pic-img'))
        .map((img) => img.getAttribute('data-src') || img.getAttribute('src'))
        .filter(Boolean);

      result.reviews.push({
        id: el.getAttribute('data-review-id') || '',
        rating: parseFloat(ratingAttr || '0'),
        title: text(el.querySelector('.jdgm-rev__title')),
        body: text(el.querySelector('.jdgm-rev__body')),
        author: text(el.querySelector('.jdgm-rev__author')),
        location: text(el.querySelector('.jdgm-rev__location')),
        verified: (el.getAttribute('data-verified-buyer') === 'true')
          || !!el.querySelector('.jdgm-rev__verified-icon, .jdgm-verified-badge'),
        date: dateIso,
        dateLabel: text(timestampEl),
        pictures,
      });
    });

    // Pagination — count distinct page numbers in pagination links
    const pageLinks = doc.querySelectorAll('.jdgm-paginate__page, [data-page]');
    let maxPage = 1;
    pageLinks.forEach((a) => {
      const p = parseInt(a.getAttribute('data-page') || a.textContent || '0', 10);
      if (Number.isFinite(p) && p > maxPage) maxPage = p;
    });
    if (maxPage <= 1 && result.total > this.perPage) {
      maxPage = Math.ceil(result.total / this.perPage);
    }
    result.pageCount = maxPage;

    // Distribution / Histogram
    doc.querySelectorAll('.jdgm-histogram__row').forEach((row) => {
      const stars = parseInt(row.getAttribute('data-rating') || row.getAttribute('data-frequency-rating') || '0', 10);
      const count = parseInt(row.getAttribute('data-frequency') || '0', 10);
      if (stars > 0 && stars <= 5) result.distribution[stars] = count;
    });

    // Judge.me product UUID — verschiedene mögliche Stellen
    const writeLink = doc.querySelector('a[href*="/product_reviews/"]');
    if (writeLink) {
      const m = writeLink.getAttribute('href').match(/product_reviews\/([a-f0-9-]+)/i);
      if (m) result.judgemeProductUuid = m[1];
    }
    if (!result.judgemeProductUuid) {
      const scoped = doc.querySelector('[data-product-id]');
      if (scoped) {
        const candidate = scoped.getAttribute('data-product-id');
        if (/^[a-f0-9-]{20,}$/i.test(candidate)) result.judgemeProductUuid = candidate;
      }
    }

    return result;
  }

  render() {
    const { reviews, total, average, distribution, pageCount } = this.state;
    const hasReviews = reviews.length > 0 || total > 0;

    if (!hasReviews) {
      this.renderEmptyState();
      return;
    }

    this.innerHTML = `
      <div class="lora-reviews__header">
        <div class="lora-reviews__score">
          <div class="lora-reviews__average">${this.formatNumber(average, 1)}</div>
          <div class="lora-reviews__average-stars">${this.starsMarkup(average)}</div>
          <div class="lora-reviews__count">${total} ${total === 1 ? 'Bewertung' : 'Bewertungen'}</div>
        </div>
        ${Object.keys(distribution).length ? this.distributionMarkup(distribution, total) : ''}
        <button type="button" class="lora-reviews__cta" data-action="write">${this.escapeHtml(this.writeReviewLabel)}</button>
      </div>
      ${reviews.length ? `
        <ul class="lora-reviews__list">
          ${reviews.map((r) => this.reviewMarkup(r)).join('')}
        </ul>
      ` : ''}
      ${pageCount > 1 ? this.paginationMarkup(pageCount) : ''}
    `;

    this.bindEvents();
    this.announceStatus(true);
  }

  renderEmptyState(message) {
    const msg = message || 'Noch keine Bewertungen — schreib die erste!';
    this.innerHTML = `
      <div class="lora-reviews__empty">
        <p class="lora-reviews__empty-text">${this.escapeHtml(msg)}</p>
        <button type="button" class="lora-reviews__cta" data-action="write">${this.escapeHtml(this.writeReviewLabel)}</button>
      </div>
    `;
    this.bindEvents();
    this.announceStatus(false);
  }

  // Signalisiert, ob es Produktbewertungen gibt — Shop-Bewertungen-Fallback hoert darauf.
  announceStatus(hasReviews) {
    document.dispatchEvent(new CustomEvent('lora:reviews:loaded', {
      detail: { hasReviews: hasReviews, total: this.state.total, productId: this.productId },
    }));
  }

  reviewMarkup(r) {
    const name = this.escapeHtml(r.author || 'Anonym');
    const dateLabel = r.dateLabel || (r.date ? this.formatDate(r.date) : '');
    const title = r.title ? `<h3 class="lora-reviews__item-title">${this.escapeHtml(r.title)}</h3>` : '';
    const body = r.body ? `<p class="lora-reviews__item-body">${this.escapeHtml(r.body)}</p>` : '';
    const pictures = (r.pictures || [])
      .map((src) => `<img loading="lazy" class="lora-reviews__pic" src="${this.escapeHtml(src)}" alt="">`)
      .join('');

    return `
      <li class="lora-reviews__item">
        <header class="lora-reviews__item-head">
          <div class="lora-reviews__item-stars">${this.starsMarkup(r.rating)}</div>
          <div class="lora-reviews__item-meta">
            <span class="lora-reviews__item-author">${name}</span>
            ${r.verified ? '<span class="lora-reviews__item-verified" title="Verifizierter Kauf">Verifiziert</span>' : ''}
            ${dateLabel ? `<time class="lora-reviews__item-date"${r.date ? ` datetime="${this.escapeHtml(r.date)}"` : ''}>${this.escapeHtml(dateLabel)}</time>` : ''}
          </div>
        </header>
        ${title}
        ${body}
        ${pictures ? `<div class="lora-reviews__pics">${pictures}</div>` : ''}
      </li>
    `;
  }

  distributionMarkup(dist, total) {
    const buckets = [5, 4, 3, 2, 1];
    return `
      <div class="lora-reviews__dist" aria-hidden="true">
        ${buckets
          .map((n) => {
            const c = dist[n] || 0;
            const pct = total ? Math.round((c / total) * 100) : 0;
            return `
              <div class="lora-reviews__dist-row">
                <span class="lora-reviews__dist-label">${n}★</span>
                <span class="lora-reviews__dist-bar"><span class="lora-reviews__dist-fill" style="width:${pct}%"></span></span>
                <span class="lora-reviews__dist-count">${c}</span>
              </div>`;
          })
          .join('')}
      </div>
    `;
  }

  paginationMarkup(pageCount) {
    const max = Math.min(pageCount, 8);
    let html = '<nav class="lora-reviews__pagination" aria-label="Reviews-Seitennavigation">';
    if (this.page > 1) html += `<button type="button" data-page="${this.page - 1}" class="lora-reviews__page">‹</button>`;
    for (let i = 1; i <= max; i++) {
      const cls = i === this.page ? 'lora-reviews__page lora-reviews__page--current' : 'lora-reviews__page';
      html += `<button type="button" data-page="${i}" class="${cls}">${i}</button>`;
    }
    if (this.page < pageCount) html += `<button type="button" data-page="${this.page + 1}" class="lora-reviews__page">›</button>`;
    html += '</nav>';
    return html;
  }

  starsMarkup(rating) {
    const r = Number(rating) || 0;
    let html = '<span class="lora-reviews__stars" aria-label="' + r.toFixed(1) + ' von 5 Sternen">';
    for (let i = 1; i <= 5; i++) {
      const cls = i <= Math.round(r) ? 'lora-reviews__star lora-reviews__star--on' : 'lora-reviews__star';
      html += `<svg class="${cls}" viewBox="0 0 32 32" aria-hidden="true"><path d="M16 2l4.2 8.5 9.4 1.4-6.8 6.6 1.6 9.4L16 23.5l-8.4 4.4 1.6-9.4-6.8-6.6 9.4-1.4z"/></svg>`;
    }
    html += '</span>';
    return html;
  }

  bindEvents() {
    this.querySelectorAll('[data-page]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        this.page = parseInt(btn.dataset.page, 10);
        await this.load();
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
    this.querySelectorAll('[data-action="write"]').forEach((btn) => {
      btn.addEventListener('click', () => this.openWriteForm());
    });
  }

  openWriteForm() {
    if (this._dialog) {
      this._dialogOpenedAt = Date.now();
      this._dialog.showModal();
      return;
    }

    const dialog = document.createElement('dialog');
    dialog.className = 'lora-reviews__dialog';
    dialog.innerHTML = `
      <form class="lora-reviews__form" novalidate>
        <header class="lora-reviews__form-head">
          <h3 class="lora-reviews__form-title">${this.escapeHtml(this.writeReviewLabel)}</h3>
          <p class="lora-reviews__form-subtitle">${this.escapeHtml(this.productTitle)}</p>
          <button type="button" class="lora-reviews__form-close" aria-label="Schließen" data-action="close">×</button>
        </header>

        <div class="lora-reviews__form-body">
          <fieldset class="lora-reviews__form-rating" data-rating="0">
            <legend class="lora-reviews__form-label">Deine Bewertung *</legend>
            <div class="lora-reviews__form-stars" role="radiogroup" aria-label="Sterne-Bewertung">
              ${[1, 2, 3, 4, 5].map((n) => `
                <button type="button" class="lora-reviews__form-star" data-star="${n}" aria-label="${n} Stern${n > 1 ? 'e' : ''}" role="radio" aria-checked="false">
                  <svg viewBox="0 0 32 32" aria-hidden="true"><path d="M16 2l4.2 8.5 9.4 1.4-6.8 6.6 1.6 9.4L16 23.5l-8.4 4.4 1.6-9.4-6.8-6.6 9.4-1.4z"/></svg>
                </button>
              `).join('')}
            </div>
          </fieldset>

          <label class="lora-reviews__form-field">
            <span class="lora-reviews__form-label">Name *</span>
            <input type="text" name="name" required maxlength="200" autocomplete="name" class="lora-reviews__form-input">
          </label>

          <label class="lora-reviews__form-field">
            <span class="lora-reviews__form-label">E-Mail *</span>
            <input type="email" name="email" required maxlength="200" autocomplete="email" class="lora-reviews__form-input">
            <span class="lora-reviews__form-hint">Wird nicht veröffentlicht.</span>
          </label>

          <label class="lora-reviews__form-field">
            <span class="lora-reviews__form-label">Titel</span>
            <input type="text" name="title" maxlength="200" class="lora-reviews__form-input">
          </label>

          <label class="lora-reviews__form-field">
            <span class="lora-reviews__form-label">Deine Bewertung *</span>
            <textarea name="body" required maxlength="5000" rows="5" class="lora-reviews__form-textarea"></textarea>
          </label>

          <!-- Honeypot — fuer Bots sichtbar, fuer User unsichtbar. Wenn ausgefuellt: Spam. -->
          <div class="lora-reviews__form-honeypot" aria-hidden="true">
            <label>
              Website (bitte leer lassen)
              <input type="text" name="website" tabindex="-1" autocomplete="off">
            </label>
          </div>

          <p class="lora-reviews__form-error" hidden></p>
        </div>

        <footer class="lora-reviews__form-foot">
          <button type="button" class="lora-reviews__form-cancel" data-action="close">Abbrechen</button>
          <button type="submit" class="lora-reviews__cta lora-reviews__form-submit">Bewertung absenden</button>
        </footer>

        <div class="lora-reviews__form-success" hidden>
          <h4 class="lora-reviews__form-title">Danke!</h4>
          <p>Deine Bewertung ist eingegangen. Sie erscheint nach kurzer Prüfung — meist innerhalb weniger Minuten.</p>
          <button type="button" class="lora-reviews__cta" data-action="close">Schließen</button>
        </div>
      </form>
    `;

    document.body.appendChild(dialog);
    this._dialog = dialog;
    this._dialogOpenedAt = Date.now();
    this._wireDialog(dialog);
    dialog.showModal();
  }

  _wireDialog(dialog) {
    const form = dialog.querySelector('form');
    const ratingGroup = dialog.querySelector('.lora-reviews__form-rating');
    const stars = dialog.querySelectorAll('.lora-reviews__form-star');
    const errorEl = dialog.querySelector('.lora-reviews__form-error');
    const successEl = dialog.querySelector('.lora-reviews__form-success');
    const bodyEl = dialog.querySelector('.lora-reviews__form-body');
    const footEl = dialog.querySelector('.lora-reviews__form-foot');
    const headEl = dialog.querySelector('.lora-reviews__form-head');

    let rating = 0;
    const setRating = (n) => {
      rating = n;
      ratingGroup.dataset.rating = String(n);
      stars.forEach((s) => {
        const v = parseInt(s.dataset.star, 10);
        s.classList.toggle('lora-reviews__form-star--on', v <= n);
        s.setAttribute('aria-checked', v === n ? 'true' : 'false');
      });
    };

    stars.forEach((s) => {
      s.addEventListener('click', () => setRating(parseInt(s.dataset.star, 10)));
      s.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
          e.preventDefault();
          setRating(Math.min(5, rating + 1));
          stars[Math.min(4, rating - 1 + 1)]?.focus();
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
          e.preventDefault();
          setRating(Math.max(1, rating - 1));
          stars[Math.max(0, rating - 1)]?.focus();
        }
      });
    });

    dialog.querySelectorAll('[data-action="close"]').forEach((btn) => {
      btn.addEventListener('click', () => dialog.close());
    });

    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) dialog.close();
    });

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      errorEl.hidden = true;

      if (rating < 1) {
        errorEl.textContent = 'Bitte wähle eine Stern-Bewertung.';
        errorEl.hidden = false;
        return;
      }

      const fd = new FormData(form);
      const elapsed = Date.now() - (this._dialogOpenedAt || Date.now());
      const payload = {
        external_id: this.productId,
        name: fd.get('name') || '',
        email: fd.get('email') || '',
        title: fd.get('title') || '',
        body: fd.get('body') || '',
        rating,
        // Anti-Spam Felder
        website: fd.get('website') || '',
        elapsed_ms: elapsed,
      };

      const submitBtn = form.querySelector('.lora-reviews__form-submit');
      submitBtn.disabled = true;
      submitBtn.textContent = 'Wird gesendet…';

      try {
        const endpoint = this.proxyUrl ? `${this.proxyUrl}/reviews` : 'https://judge.me/api/v1/reviews';
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (res.status === 429) {
          const err = await res.json().catch(() => ({}));
          const min = err.retryAfterSeconds ? Math.ceil(err.retryAfterSeconds / 60) : 60;
          throw new Error(`Zu viele Bewertungen von dieser Verbindung. Bitte in ${min} Minuten erneut versuchen.`);
        }
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || `HTTP ${res.status}`);
        }
        // Success — show thank-you state
        bodyEl.hidden = true;
        footEl.hidden = true;
        headEl.hidden = true;
        successEl.hidden = false;
      } catch (err) {
        console.error('[lora-reviews] submit failed', err);
        errorEl.textContent = `Konnte nicht gesendet werden: ${err.message}`;
        errorEl.hidden = false;
        submitBtn.disabled = false;
        submitBtn.textContent = 'Bewertung absenden';
      }
    });
  }

  formatNumber(n, digits) {
    return Number(n || 0).toFixed(digits);
  }

  formatDate(iso) {
    try {
      return new Intl.DateTimeFormat('de-DE', { year: 'numeric', month: 'short', day: 'numeric' }).format(new Date(iso));
    } catch {
      return '';
    }
  }

  escapeHtml(s) {
    return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }
}

function text(el) {
  return el ? el.textContent.replace(/\s+/g, ' ').trim() : '';
}

if (!customElements.get('lora-reviews')) {
  customElements.define('lora-reviews', LoraReviews);
}
